import type { AuthSession } from "../../runtime/authSession";

/**
 * Wait for auth status to resolve to either "logged-in" or "no-user"
 * This prevents race conditions where API calls are made before Firebase auth has initialized
 * @param timeoutMs - Maximum time to wait in milliseconds (default: 10000)
 * @returns Promise that resolves when auth is ready or rejects on timeout
 */
export const waitForAuthReady = async (
  auth: AuthSession,
  timeoutMs = 10000,
): Promise<void> => {
  if (auth.signal.aborted) throw new Error("Auth session has ended");
  const authStore = auth.store;
  const authStatus = authStore.getState().status;

  // If status is no-user, we're ready (no need to wait for currentUser)
  if (authStatus === "no-user") {
    console.log("waitForAuthReady :: no user, returning immediately");
    return;
  }

  // If status is logged-in AND currentUser exists, we're ready
  if (authStatus === "logged-in" && auth.currentUser()) {
    console.log(
      "waitForAuthReady :: logged in with currentUser, returning immediately",
    );
    return;
  }

  // Otherwise, we need to wait (either auth is pending, or logged-in but no currentUser yet)
  console.log("waitForAuthReady :: need to wait for auth to be fully ready");

  const startTime = Date.now();

  let loginUnsubscribe: (() => void) | undefined;
  const loginStatusPromise = new Promise<void>((resolve) => {
    const resolveIfReady = (status: string) => {
      if (status === "logged-in" || status === "no-user") {
        console.log(
          "waitForAuthReady :: loginStatusPromise resolved",
          `time: ${Date.now() - startTime}ms`,
        );
        loginUnsubscribe?.();
        resolve();
        return true;
      }
      return false;
    };
    // Immediate check to avoid missing a just-resolved state.
    if (resolveIfReady(authStore.getState().status)) return;
    loginUnsubscribe = authStore.subscribe((state) => {
      console.log(
        "waitForAuthReady :: loginStatusPromise :: auth status changed",
        state.status,
      );
      resolveIfReady(state.status);
    });
  });

  let firebaseUnsubscribe: (() => void) | undefined;
  let authStoreUnsubscribe: (() => void) | undefined;

  const firebaseAuthPromise = new Promise<void>((resolve) => {
    // Immediate check in case user is already available.
    if (auth.currentUser()) return resolve();

    const endPromise = () => {
      authStoreUnsubscribe?.();
      firebaseUnsubscribe?.();
      resolve();
    };

    // if we don't have a user for sure, we can end the promise
    if (authStore.getState().status === "no-user") {
      console.log(
        "waitForAuthReady :: firebaseAuthPromise :: no user",
        `time: ${Date.now() - startTime}ms`,
      );
      endPromise();
      return;
    }

    authStoreUnsubscribe = authStore.subscribe((state) => {
      if (state.status === "no-user") {
        console.log(
          "waitForAuthReady :: firebaseAuthPromise :: no user",
          `time: ${Date.now() - startTime}ms`,
        );
        endPromise();
      }
    });

    firebaseUnsubscribe = auth.subscribe((user) => {
      if (user) {
        console.log(
          "waitForAuthReady :: firebaseAuthPromise resolved",
          `time: ${Date.now() - startTime}ms`,
        );
        endPromise();
      }
    });
  });

  const bothAuthPromises = Promise.all([
    loginStatusPromise,
    firebaseAuthPromise,
  ]);

  let timer: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // Best-effort cleanup on timeout
      loginUnsubscribe?.();
      firebaseUnsubscribe?.();
      authStoreUnsubscribe?.();
      reject(new Error(`Auth status wait timeout after ${timeoutMs}ms`));
    }, timeoutMs);
  });
  let stopWaiting: () => void;
  const endedPromise = new Promise<never>((_, reject) => {
    stopWaiting = () => {
      clearTimeout(timer!);
      loginUnsubscribe?.();
      firebaseUnsubscribe?.();
      authStoreUnsubscribe?.();
      reject(new Error("Auth session has ended"));
    };
    auth.signal.addEventListener("abort", stopWaiting, { once: true });
  });

  try {
    await Promise.race([bothAuthPromises, timeoutPromise, endedPromise]);

    console.log(
      "waitForAuthReady :: auth resolved",
      `status: ${authStore.getState().status}, time: ${Date.now() - startTime}ms`,
    );
  } catch (error) {
    console.error("waitForAuthReady :: auth wait failed", error);
    throw error;
  } finally {
    clearTimeout(timer!);
    auth.signal.removeEventListener("abort", stopWaiting!);
    loginUnsubscribe?.();
    firebaseUnsubscribe?.();
    authStoreUnsubscribe?.();
  }
};
